import { useMemo } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import {
	StakedTier,
	StakedTierSelectValue,
} from 'src/components/Modals/FeeTierModal/types';
import { STAKED_TIER_SELECT_OPTIONS } from 'src/components/Modals/FeeTierModal/consts';
import { capitalize } from 'lodash';
import BottomDrawerSelect from 'src/components/Inputs/BottomDrawerSelect/BottomDrawerSelect';
import Select from 'src/components/primitives/Select/Select';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';

interface Props {
	selectedStakedTier: StakedTierSelectValue;
	userCurrentStakedTierDiscount: number;
	onSelect: (stakedTier: StakedTierSelectValue) => void;
}

const LABEL = 'Booster / DRIFT staked';

export default function FeeTierModalStakedTierSelect({
	selectedStakedTier,
	userCurrentStakedTierDiscount,
	onSelect,
}: Props) {
	const isMobileScreenSize = useIsMobileScreenSize();
	const options = useMemo(() => {
		return STAKED_TIER_SELECT_OPTIONS.map((option) => ({
			label: (
				<>
					<span>{capitalize(option.title)}</span>
					<span>
						{getDisplayValue(
							option,
							option.discount === userCurrentStakedTierDiscount
						)}
					</span>
				</>
			),
			value: option,
		}));
	}, [userCurrentStakedTierDiscount]);

	const { open, onOpenChange, selectOptions, value, onValueChange } = useSelect(
		{
			options,
			onSelectedValueChange: onSelect,
			selectedValue: selectedStakedTier,
		}
	);

	if (isMobileScreenSize) {
		return (
			<BottomDrawerSelect
				label={LABEL}
				triggerValue={getDisplayValue(selectedStakedTier, false)}
				options={selectOptions}
				onSelect={onValueChange}
				selectedValue={value}
				noSelectedCheckmark
			/>
		);
	}

	return (
		<Select.Root
			open={open}
			onOpenChange={onOpenChange}
			value={value}
			onValueChange={onValueChange}
		>
			<Select.Trigger label={LABEL}>
				{getDisplayValue(selectedStakedTier, false)}
			</Select.Trigger>
			<Select.Options>
				{selectOptions.map((option) => (
					<Select.Option
						key={option.value}
						value={option.value}
						noSelectedCheckmark
					>
						{option.label}
					</Select.Option>
				))}
			</Select.Options>
		</Select.Root>
	);
}

function getDisplayValue(
	stakedTier: StakedTier | undefined,
	addSelectedIndicator: boolean
) {
	if (!stakedTier) {
		return '';
	}

	return (
		<span className="flex items-center gap-x-sm">
			{addSelectedIndicator && (
				<span className="text-md text-purple-link" data-selected-indicator>
					•
				</span>
			)}
			{stakedTier.discount * 100}% / {stakedTier.display} DRIFT
		</span>
	);
}
